import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Camera, RotateCcw, Check, X } from 'lucide-react';

const DocumentScanner = ({ 
  onCapture, 
  onCancel,
  mode = 'document', // 'document' | 'selfie'
  title = 'Escanear Documento',
  instructions = 'Coloca tu documento dentro del recuadro'
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const nativeCameraRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [cameraFailed, setCameraFailed] = useState(false);

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|FxiOS/i.test(navigator.userAgent) 
    || ('ontouchstart' in window) 
    || (navigator.maxTouchPoints > 0)
    || (window.innerWidth <= 1024);

  // Try live camera first
  const startCamera = useCallback(async () => {
    try {
      const constraints = {
        video: {
          facingMode: mode === 'selfie' ? 'user' : 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setIsReady(true);
        };
      }
    } catch {
      // Camera failed → fall back to native camera silently
      setCameraFailed(true);
      // Auto-open native camera input
      setTimeout(() => {
        if (nativeCameraRef.current) {
          nativeCameraRef.current.value = '';
          nativeCameraRef.current.click();
        }
      }, 300);
    }
  }, [mode]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsReady(false);
  }, [stream]);

  // Start camera on mount (mobile only)
  useEffect(() => {
    if (isMobile) {
      startCamera();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []); // start camera once on mount

  // Capture from live video
  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      const file = new File([blob], `${mode}_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const imageUrl = URL.createObjectURL(blob);
      setCapturedImage({ file, url: imageUrl });
    }, 'image/jpeg', 0.9);
  }, [mode]);

  // Countdown capture
  const startCapture = useCallback(() => {
    setCountdown(3);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          captureImage();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [captureImage]);

  // Handle native camera file
  const handleNativeFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) {
      // User cancelled native camera → if camera also failed, let them try again or cancel
      return;
    }
    // Compress if needed
    const maxSize = 4 * 1024 * 1024;
    if (file.size > maxSize) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new window.Image();
      img.onload = () => {
        const scale = Math.min(1920 / img.width, 1920 / img.height, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          const compressed = new File([blob], file.name, { type: 'image/jpeg' });
          setCapturedImage({ file: compressed, url: URL.createObjectURL(blob) });
        }, 'image/jpeg', 0.85);
      };
      img.src = URL.createObjectURL(file);
    } else {
      setCapturedImage({ file, url: URL.createObjectURL(file) });
    }
  }, []);

  const confirmCapture = useCallback(() => {
    if (capturedImage) {
      stopCamera();
      onCapture(capturedImage.file, capturedImage.url);
    }
  }, [capturedImage, onCapture, stopCamera]);

  const retryCapture = useCallback(() => {
    setCapturedImage(null);
    if (cameraFailed) {
      // Re-open native camera
      setTimeout(() => {
        if (nativeCameraRef.current) {
          nativeCameraRef.current.value = '';
          nativeCameraRef.current.click();
        }
      }, 200);
    } else if (!stream) {
      startCamera();
    }
  }, [cameraFailed, stream, startCamera]);

  const handleCancel = useCallback(() => {
    stopCamera();
    onCancel();
  }, [stopCamera, onCancel]);

  // Open native camera manually (fallback button)
  const openNativeCamera = useCallback(() => {
    if (nativeCameraRef.current) {
      nativeCameraRef.current.value = '';
      nativeCameraRef.current.click();
    }
  }, []);

  // Desktop: show message to use phone
  if (!isMobile) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        <div className="bg-green-600 text-white p-4 flex items-center justify-between">
          <button onClick={handleCancel} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-green-700 transition" data-testid="scanner-cancel-btn">
            <X size={24} />
          </button>
          <h2 className="text-base font-bold">{title}</h2>
          <div className="w-10"></div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <Camera size={36} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-3">Usa tu celular</h2>
            <p className="text-slate-500 mb-6">
              Para verificar tu identidad necesitas completar el registro desde tu telefono.
            </p>
            <button onClick={handleCancel} className="bg-green-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-green-700 transition">
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="document-scanner">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 flex items-center justify-between flex-shrink-0">
        <button onClick={handleCancel} className="text-white text-2xl font-bold" data-testid="scanner-cancel-btn">
          <X size={24} />
        </button>
        <h2 className="text-lg font-bold">{title}</h2>
        <div className="w-8"></div>
      </div>

      {/* Hidden native camera input (fallback) — ONLY camera, no gallery */}
      <input
        ref={nativeCameraRef}
        type="file"
        accept="image/*"
        capture={mode === 'selfie' ? 'user' : 'environment'}
        onChange={handleNativeFile}
        className="sr-only"
        data-testid="native-camera-input"
      />

      {/* Camera area */}
      <div className="flex-1 relative bg-black overflow-hidden">
        {capturedImage ? (
          /* === Preview captured image === */
          <img 
            src={capturedImage.url} 
            alt="Captura" 
            className="w-full h-full object-cover"
            data-testid="captured-preview"
          />
        ) : cameraFailed ? (
          /* === Camera failed: waiting for native camera result === */
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
            <div className="text-center p-6 max-w-sm">
              <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera size={32} className="text-green-400" />
              </div>
              <p className="text-white text-lg font-bold mb-2">
                {mode === 'selfie' ? 'Toma tu selfie' : 'Saca la foto'}
              </p>
              <p className="text-slate-400 text-sm mb-6">
                Se abrira la camara de tu telefono
              </p>
              <button
                onClick={openNativeCamera}
                className="bg-green-600 text-white px-8 py-4 rounded-2xl font-bold text-lg active:scale-[0.97] transition w-full"
                data-testid="open-native-camera-btn"
              >
                <Camera size={20} className="inline mr-2" />
                {mode === 'selfie' ? 'Abrir camara frontal' : 'Abrir camara'}
              </button>
            </div>
          </div>
        ) : (
          /* === Live camera stream (the tech look) === */
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: mode === 'selfie' ? 'scaleX(-1)' : 'none' }}
            />

            {/* Frame overlay */}
            <div className="absolute inset-0 pointer-events-none">
              {mode === 'document' ? (
                <>
                  <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                  <div 
                    className="absolute left-1/2 top-1/2 bg-transparent"
                    style={{ 
                      width: '88%',
                      maxWidth: '420px',
                      aspectRatio: '1.585/1',
                      transform: 'translate(-50%, -50%)',
                      boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)'
                    }}
                  >
                    <div className="absolute -top-1 -left-1 w-10 h-10 border-t-[3px] border-l-[3px] border-green-400 rounded-tl-md"></div>
                    <div className="absolute -top-1 -right-1 w-10 h-10 border-t-[3px] border-r-[3px] border-green-400 rounded-tr-md"></div>
                    <div className="absolute -bottom-1 -left-1 w-10 h-10 border-b-[3px] border-l-[3px] border-green-400 rounded-bl-md"></div>
                    <div className="absolute -bottom-1 -right-1 w-10 h-10 border-b-[3px] border-r-[3px] border-green-400 rounded-br-md"></div>
                    <div className="absolute inset-x-2 h-0.5 bg-green-400 opacity-60 animate-scan"></div>
                  </div>
                </>
              ) : (
                <>
                  <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                  <div 
                    className="absolute left-1/2 top-1/2 rounded-full"
                    style={{
                      width: '250px',
                      height: '320px',
                      transform: 'translate(-50%, -50%)',
                      boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)'
                    }}
                  ></div>
                  <div 
                    className="absolute left-1/2 top-1/2 rounded-full border-[3px] border-green-400"
                    style={{
                      width: '250px',
                      height: '320px',
                      transform: 'translate(-50%, -50%)'
                    }}
                  ></div>
                </>
              )}
            </div>

            {/* Countdown */}
            {countdown && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
                <div className="text-white text-8xl font-bold animate-pulse">
                  {countdown}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-gray-800 text-white text-center py-3 px-4 flex-shrink-0">
        <p className="text-sm">
          {capturedImage 
            ? (mode === 'document' ? 'Se ve completo y claro?' : 'Se ve bien tu cara?')
            : instructions
          }
        </p>
      </div>

      {/* Controls */}
      <div className="bg-gray-900 p-4 flex justify-center gap-4 flex-shrink-0 safe-area-bottom">
        {capturedImage ? (
          <>
            <button
              onClick={retryCapture}
              className="flex-1 max-w-xs flex items-center justify-center gap-2 bg-gray-600 text-white py-4 rounded-xl font-bold text-lg"
              data-testid="retry-btn"
            >
              <RotateCcw size={20} />
              Repetir
            </button>
            <button
              onClick={confirmCapture}
              className="flex-1 max-w-xs flex items-center justify-center gap-2 bg-green-600 text-white py-4 rounded-xl font-bold text-lg"
              data-testid="confirm-btn"
            >
              <Check size={20} />
              Usar foto
            </button>
          </>
        ) : !cameraFailed ? (
          /* Live camera controls */
          <button
            onClick={startCapture}
            disabled={!isReady || countdown !== null}
            className="w-20 h-20 bg-white rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg border-4 border-green-500"
            data-testid="capture-btn"
          >
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
              <Camera size={28} className="text-white" />
            </div>
          </button>
        ) : null}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      <style>{`
        @keyframes scan {
          0% { top: 10%; }
          50% { top: 90%; }
          100% { top: 10%; }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
        .safe-area-bottom {
          padding-bottom: max(16px, env(safe-area-inset-bottom));
        }
      `}</style>
    </div>
  );
};

export default DocumentScanner;
